/**
 * Проверка, открывается ли сайт партнёра в браузере пользователя.
 *
 * Отследить провал загрузки самого iframe нельзя: на кросс-доменный фрейм браузер
 * стреляет `load` даже когда показывает свою страницу ошибки (chrome-error://…),
 * `onerror` не срабатывает, а contentDocument закрыт Same-Origin Policy.
 * Поэтому дёргаем origin партнёра отдельным запросом: с `mode: 'no-cors'` ответ
 * непрозрачный (читать в нём нечего), но сам факт того, что промис зарезолвился,
 * означает, что соединение и TLS-рукопожатие прошли. Недоверенный сертификат
 * (ERR_CERT_AUTHORITY_INVALID у сайтов на российских УЦ) роняет fetch в TypeError.
 */

import { makeEnum } from '../constants/constantsUtils';

// Пока идёт проверка, на странице висит прелоадер, поэтому бюджет времени жёсткий:
// 1200 + 400 + 1200 = не больше 2.8 с в самом плохом случае. Реально недоступный
// хост отваливается за ~100 мс (TLS-рукопожатие не проходит), так что обычная
// задержка до вердикта — меньше секунды.
const ATTEMPT_TIMEOUT_MS = 1200;
const RETRY_DELAY_MS = 400;

const CONNECTION_STATUS = makeEnum(
  // Соединение установилось. Что именно ответил сервер, нам не важно:
  // 200, 404 и 500 одинаково означают, что до сайта достучались.
  'CONNECTED',
  // Браузер оборвал запрос: недоверенный сертификат, недоступный хост, обрыв сети.
  'CONNECTION_FAILED',
  // Ответа не дождались за отведённое время — сайт может быть и рабочим, просто медленным.
  'NO_ANSWER',
);

const wait = ms => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

const checkOrigin = async (origin) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), ATTEMPT_TIMEOUT_MS);

  try {
    await fetch(origin, {
      method: 'HEAD',
      mode: 'no-cors',
      cache: 'no-store',
      credentials: 'omit',
      signal: controller.signal,
    });
    return CONNECTION_STATUS.CONNECTED;
  } catch (error) {
    return controller.signal.aborted
      ? CONNECTION_STATUS.NO_ANSWER
      : CONNECTION_STATUS.CONNECTION_FAILED;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Возвращает true, только если мы убедились, что сайт партнёра не открывается.
 *
 * Сообщение про сертификаты показывается ВМЕСТО страницы партнёра, поэтому
 * действуем только по однозначному сигналу — двум оборванным соединениям подряд.
 * NO_ANSWER однозначным сигналом не является: он означает «мы ничего не выяснили»,
 * а не «сайт лежит». Медленный, но рабочий сайт партнёра в этом случае
 * подменился бы сообщением о том, что у пользователя не установлены сертификаты.
 */
export const isPartnerSiteUnavailable = async (iframeUrl) => {
  if (!window.fetch || !window.AbortController) {
    return false;
  }

  let origin;

  try {
    ({ origin } = new URL(iframeUrl, window.location.href));
  } catch (error) {
    return false;
  }

  // Проверяем origin, а не саму ссылку из iframe: по ней уходят токен и utm-метки,
  // повторный запрос накрутил бы партнёру статистику переходов, а блокировщики
  // режут ссылки с utm-метками и дали бы ложное срабатывание. Сертификат
  // всё равно один на хост, так что корня достаточно.
  const firstAttempt = await checkOrigin(origin);

  if (firstAttempt !== CONNECTION_STATUS.CONNECTION_FAILED) {
    return false;
  }

  // Вторая попытка — страховка от разовой сетевой помехи.
  await wait(RETRY_DELAY_MS);

  const secondAttempt = await checkOrigin(origin);

  return secondAttempt === CONNECTION_STATUS.CONNECTION_FAILED;
};

export default isPartnerSiteUnavailable;
